/**
 * sessionManager — owns idle and absolute session lifetime for the ProTeams web portal.
 *
 * Design principles:
 *
 * 1. TIMESTAMP-BASED expiry stored in localStorage.
 *    Browser background-tab timer throttling (Chrome clamps setInterval to ~1/min for
 *    inactive tabs) cannot cause missed timeouts because every _tick() and every
 *    visibility-restore check compares Date.now() against the stored expiry timestamp
 *    rather than trusting accumulated interval ticks.
 *
 * 2. MEANINGFUL INTERACTION ONLY resets the idle timer.
 *    mousemove, keydown, mousedown, touchstart are treated as user activity.
 *    Polling, WebSocket traffic, auto-refresh, auto-save, and setInterval callbacks
 *    must NOT call resetTimer() — only genuine user gestures should.
 *    (scroll is intentionally excluded — see ACTIVITY_EVENTS comment below.)
 *
 * 3. SESSION STATE SURVIVES PAGE RELOADS within the same browser session.
 *    Timestamps are persisted in localStorage so that a page refresh does not silently
 *    reset the idle window. If the user was idle for 25 min before refreshing, they
 *    have 5 min remaining after the reload — not a fresh 30 min.
 *    sessionManager.checkStaleSession() must be called on every app-load / auth-restore
 *    BEFORE rendering authenticated state to enforce this.
 *
 * 4. CROSS-TAB SYNC via BroadcastChannel ("pc-session").
 *    All authenticated tabs stay aligned for: warning shown, warning dismissed,
 *    idle timer reset, and forced logout. Falls back gracefully to single-tab
 *    behaviour in environments where BroadcastChannel is unavailable.
 *
 * Public API:
 *   sessionManager.start()             — call after successful authentication
 *   sessionManager.end()               — call at the start of every logout path
 *   sessionManager.resetTimer()        — call after a confirmed user-initiated action
 *   sessionManager.onTimeout(cb)       — register the forced-logout callback
 *   sessionManager.onWarning(cb)       — register the warning-modal open callback
 *   sessionManager.onWarningDismiss(cb)— register the warning-modal dismiss callback
 *   sessionManager.checkStaleSession() — check on app/auth restore before rendering
 *   sessionManager.getIdleExpiry()     — get the current idle-expiry timestamp (ms)
 */

// ─── constants ───────────────────────────────────────────────────────────────

const IDLE_TIMEOUT_MS     = 30 * 60 * 1000;       // 30 minutes
const ABSOLUTE_TIMEOUT_MS = 8  * 60 * 60 * 1000;  // 8 hours
const WARNING_BEFORE_MS   = 2  * 60 * 1000;        // warn 2 min before idle expiry (28-min mark)
const CHECK_INTERVAL_MS   = 15 * 1000;             // poll every 15 s

// localStorage keys — also in App.js logoutuser() keysToRemove list as canonical cleanup
const IDLE_EXPIRY_KEY     = "pcIdleExpiry";
const ABSOLUTE_EXPIRY_KEY = "pcAbsoluteExpiry";

// Cross-tab sync channel name
const CHANNEL_NAME        = "pc-session";

// User interaction events that reset the idle timer.
// Intentionally excludes "scroll":
//   - keyboard scroll (arrow keys, Page Down, Space) is already captured by "keydown"
//   - scrollbar click is already captured by "mousedown"
//   - mobile swipe-to-scroll start is already captured by "touchstart"
//   - mouse-wheel scroll without any other gesture is too passive to qualify as
//     deliberate engagement, and could be triggered by auto-scrolling content,
//     browser extensions, or an unattended wheel — silently extending the session.
// All registered as passive listeners so touch behaviour is never blocked.
const ACTIVITY_EVENTS = ["mousemove", "keydown", "mousedown", "touchstart"];

// ─── private state ───────────────────────────────────────────────────────────

let _onTimeout       = null;
let _onWarning       = null;
let _onWarningDismiss = null;
let _interval        = null;
let _warnTimer       = null;   // precise setTimeout for the 2-min warning threshold
let _expiryTimer     = null;   // precise setTimeout for idle expiry
let _channel         = null;   // BroadcastChannel instance (null if unsupported)
let _active          = false;
let _warnFired       = false;

// ─── private helpers ─────────────────────────────────────────────────────────

function _readIdleExpiry() {
  const v = localStorage.getItem(IDLE_EXPIRY_KEY);
  return v ? parseInt(v, 10) : null;
}

function _writeIdleExpiry() {
  localStorage.setItem(IDLE_EXPIRY_KEY, String(Date.now() + IDLE_TIMEOUT_MS));
}

function _readAbsoluteExpiry() {
  const v = localStorage.getItem(ABSOLUTE_EXPIRY_KEY);
  return v ? parseInt(v, 10) : null;
}

function _writeAbsoluteExpiry() {
  localStorage.setItem(ABSOLUTE_EXPIRY_KEY, String(Date.now() + ABSOLUTE_TIMEOUT_MS));
}

function _clearExpiries() {
  localStorage.removeItem(IDLE_EXPIRY_KEY);
  localStorage.removeItem(ABSOLUTE_EXPIRY_KEY);
}

/**
 * Dismiss the active warning (if any), and optionally broadcast the reset to
 * other authenticated tabs so their warning modals close too.
 * Safe to call when _warnFired is already false — it is a no-op in that case.
 */
function _clearWarning(broadcast) {
  if (!_warnFired) return;
  _warnFired = false;
  if (_onWarningDismiss) _onWarningDismiss();
  if (broadcast && _channel) {
    try { _channel.postMessage({ type: "session:reset" }); } catch (_e) { /* ignore */ }
  }
}

/**
 * Schedule a precise setTimeout to fire the warning callback exactly when
 * (idleExpiry - WARNING_BEFORE_MS) arrives, replacing any previously scheduled
 * timer.  Reduces maximum warning delay from ~CHECK_INTERVAL_MS (~15 s) to
 * <1 ms (JS event-loop precision).  The existing _tick() check is kept as a
 * defensive fallback for background-tab setTimeout throttling.
 *
 * Must be called whenever the idle expiry timestamp is written:
 *   - start()           — fresh or resumed session
 *   - _handleActivity() — user gesture resets idle clock
 *   - resetTimer()      — explicit "Stay Signed In" reset
 *   - session:reset     — cross-tab reset received via BroadcastChannel
 */
function _scheduleWarning() {
  if (_warnTimer) {
    clearTimeout(_warnTimer);
    _warnTimer = null;
  }
  if (!_active) return;

  const idleExpiry = _readIdleExpiry();
  if (idleExpiry === null) return;

  const delay = (idleExpiry - WARNING_BEFORE_MS) - Date.now();

  if (delay <= 0) {
    // Already inside the warning window (e.g. resumed session close to expiry).
    if (!_warnFired) {
      const now       = Date.now();
      const remaining = idleExpiry - now;
      if (remaining > 0 && remaining <= WARNING_BEFORE_MS) {
        _warnFired = true;
        if (_onWarning) _onWarning(Math.ceil(remaining / 1000));
      }
    }
    return;
  }

  _warnTimer = setTimeout(() => {
    _warnTimer = null;
    if (!_active || _warnFired) return;
    // Re-read from localStorage: another tab may have reset the expiry since
    // this timer was scheduled.
    const freshExpiry = _readIdleExpiry();
    if (freshExpiry === null) return;
    const now       = Date.now();
    const remaining = freshExpiry - now;
    if (remaining > 0 && remaining <= WARNING_BEFORE_MS) {
      _warnFired = true;
      if (_onWarning) _onWarning(Math.ceil(remaining / 1000));
    }
  }, delay);
}

/**
 * Schedule a precise timeout for the exact idle-expiry moment.
 * Keeps forced logout aligned with the countdown shown in SessionWarningModal,
 * while _tick() remains a defensive fallback.
 */
function _scheduleIdleExpiry() {
  if (_expiryTimer) {
    clearTimeout(_expiryTimer);
    _expiryTimer = null;
  }
  if (!_active) return;

  const idleExpiry = _readIdleExpiry();
  if (idleExpiry === null) return;

  const delay = idleExpiry - Date.now();
  if (delay <= 0) {
    _doTimeout("idle");
    return;
  }

  _expiryTimer = setTimeout(() => {
    _expiryTimer = null;
    if (!_active) return;
    const freshExpiry = _readIdleExpiry();
    if (freshExpiry === null) return;
    if (Date.now() >= freshExpiry) {
      _doTimeout("idle");
    } else {
      // Another tab/user activity moved expiry forward — resync timer.
      _scheduleIdleExpiry();
    }
  }, delay);
}

// ─── event handlers ──────────────────────────────────────────────────────────

function _handleActivity() {
  if (!_active) return;
  _writeIdleExpiry();
  // If the warning was visible, dismiss it and broadcast the reset to other tabs.
  _clearWarning(true);
  // Reschedule the precise warning timer for the new expiry.
  _scheduleWarning();
  _scheduleIdleExpiry();
}

function _tick() {
  if (!_active) return;

  const now             = Date.now();
  const idleExpiry      = _readIdleExpiry();
  const absoluteExpiry  = _readAbsoluteExpiry();
  const idleExpired     = idleExpiry     !== null && now >= idleExpiry;
  const absoluteExpired = absoluteExpiry !== null && now >= absoluteExpiry;

  if (idleExpired || absoluteExpired) {
    _doTimeout(absoluteExpired ? "absolute" : "idle");
    return;
  }

  // Warning: fire once when within WARNING_BEFORE_MS of idle expiry.
  // _clearWarning() resets _warnFired when the user becomes active again.
  if (!_warnFired && idleExpiry !== null && (idleExpiry - now) <= WARNING_BEFORE_MS) {
    _warnFired = true;
    if (_onWarning) {
      _onWarning(Math.ceil((idleExpiry - now) / 1000));
    }
  }
}

/**
 * Internal: end the session for this tab and (unless this is a cross-tab echo)
 * broadcast the logout event to all other authenticated tabs.
 * @param {boolean} skipBroadcast  true when called because we RECEIVED a cross-tab
 *                                 logout — prevents relay-broadcast chains.
 */
function _endSession(skipBroadcast) {
  if (!_active && !_interval) return;

  _active = false;

  // Dismiss any open warning UI in this tab before teardown.
  if (_warnFired && _onWarningDismiss) _onWarningDismiss();
  _warnFired = false;

  ACTIVITY_EVENTS.forEach((evt) =>
    window.removeEventListener(evt, _handleActivity)
  );
  document.removeEventListener("visibilitychange", _onVisibilityChange);

  if (_interval) {
    clearInterval(_interval);
    _interval = null;
  }

  if (_warnTimer) {
    clearTimeout(_warnTimer);
    _warnTimer = null;
  }
  if (_expiryTimer) {
    clearTimeout(_expiryTimer);
    _expiryTimer = null;
  }

  _clearExpiries();

  // Broadcast logout to other tabs only from the initiating tab.
  if (!skipBroadcast && _channel) {
    try { _channel.postMessage({ type: "session:logout" }); } catch (_e) { /* ignore */ }
  }

  // Close the channel after broadcasting.
  if (_channel) {
    _channel.close();
    _channel = null;
  }
}

function _doTimeout(reason) {
  // Capture callback before _endSession() runs so the reference is valid.
  const cb = _onTimeout;
  _endSession(false);   // Broadcast logout to other tabs
  if (cb) cb(reason);
}

function _onVisibilityChange() {
  // Re-check immediately when the user switches back to this tab.
  // Compensates for background-tab timer throttling described in design note 1.
  if (document.visibilityState === "visible") {
    _tick();
  }
}

/**
 * Handle messages from other authenticated tabs via BroadcastChannel.
 *
 * "session:reset"  — another tab had user activity while the warning was showing;
 *                    dismiss the warning in this tab and reset _warnFired so the
 *                    next idle window fires a fresh warning.
 *
 * "session:logout" — another tab triggered a logout (manual, timeout, or 401);
 *                    mirror the logout here without re-broadcasting (skipBroadcast=true).
 */
function _onChannelMessage(event) {
  const { type } = event.data || {};

  switch (type) {
    case "session:reset":
      if (_active) {
        // The localStorage value was already updated by the other tab.
        // Clear local warning state (only if it was showing) and reschedule the
        // precise timer against the new expiry so this tab fires at the right
        // time for the next idle window.
        if (_warnFired) {
          _warnFired = false;
          if (_onWarningDismiss) _onWarningDismiss();
        }
        _scheduleWarning();
        _scheduleIdleExpiry();
      }
      break;

    case "session:logout":
      if (_active) {
        const cb = _onTimeout;
        _endSession(true);   // skipBroadcast — do NOT relay to avoid chain reactions
        if (cb) cb("cross-tab");
      }
      break;

    default:
      break;
  }
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Start (or resume) the session.
 * Safe to call multiple times — subsequent calls while already active are no-ops.
 *
 * On first login: no existing timestamps → writes fresh expiry values.
 * On page reload within an active session: existing timestamps are preserved so
 * the idle countdown is NOT reset by the reload (see design note 3 above).
 */
function start() {
  if (_active) return;

  _active    = true;
  _warnFired = false;

  // Preserve existing timestamps through page reloads (resume, don't restart).
  // Only initialise if no timestamps are present (i.e. fresh login).
  if (!_readIdleExpiry())     _writeIdleExpiry();
  if (!_readAbsoluteExpiry()) _writeAbsoluteExpiry();

  ACTIVITY_EVENTS.forEach((evt) =>
    // passive: true ensures touchstart never blocks the browser's native scroll behaviour.
    window.addEventListener(evt, _handleActivity, { passive: true })
  );
  document.addEventListener("visibilitychange", _onVisibilityChange);

  // Open the cross-tab sync channel.  Gracefully skipped if unsupported.
  if (typeof BroadcastChannel !== "undefined") {
    _channel = new BroadcastChannel(CHANNEL_NAME);
    _channel.onmessage = _onChannelMessage;
  }

  _interval = setInterval(_tick, CHECK_INTERVAL_MS);
  // Schedule a precise one-shot timer for the warning threshold so it fires
  // immediately at the 2-min mark rather than waiting up to ~15 s for the next tick.
  _scheduleWarning();
  _scheduleIdleExpiry();
}

/**
 * Force a fresh session window in the current tab without broadcasting logout.
 * Useful immediately after successful manual login where previous expiry values
 * must never invalidate the new session.
 */
function restartFresh() {
  _endSession(true);
  start();
}

/**
 * Tear down all timers, event listeners, and expiry timestamps.
 * Called automatically when a timeout fires (_doTimeout).
 * MUST be called at the start of every logout path (manual, 401, concurrent-session,
 * timeout) via App.js logoutuser() to ensure clean state.
 * Safe to call multiple times — subsequent calls while inactive are no-ops.
 */
function end() {
  _endSession(false);
}

/**
 * Explicitly reset the idle expiry timestamp.
 * Use this for confirmed user-initiated actions that are not already covered by
 * the activity event listeners (e.g. "Stay Signed In" button in the warning modal).
 * Do NOT call this from polling, WebSocket handlers, or auto-refresh callbacks.
 * If the warning was active, dismisses it and notifies all other tabs.
 */
function resetTimer() {
  if (!_active) return;
  _writeIdleExpiry();
  _clearWarning(true);   // dismiss warning and broadcast reset if it was showing
  _scheduleWarning();    // reschedule precise timer for the new idle expiry
  _scheduleIdleExpiry(); // reschedule precise timeout at exact idle-expiry instant
}

/**
 * Register the callback that fires when the idle or absolute timeout is reached.
 * Should be wired to logoutuser() in App.js.
 * @param {(reason: "idle" | "absolute" | "cross-tab") => void} cb
 */
function onTimeout(cb) {
  _onTimeout = cb;
}

/**
 * Register the callback that fires when the 2-minute warning window opens.
 * Wire to setSessionWarning(true) in App.js.
 * @param {(secondsRemaining: number) => void} cb
 */
function onWarning(cb) {
  _onWarning = cb;
}

/**
 * Register the callback that fires when the warning should be dismissed.
 * Fires on: user activity while warning is showing, resetTimer(), end().
 * Wire to setSessionWarning(false) in App.js.
 * @param {() => void} cb
 */
function onWarningDismiss(cb) {
  _onWarningDismiss = cb;
}

/**
 * Check whether stored expiry timestamps indicate a stale / expired session.
 * Call on every app-load and auth-restore BEFORE rendering any authenticated state.
 *
 * Returns "idle"     if the idle timeout has been exceeded.
 * Returns "absolute" if the 8-hour absolute lifetime has been exceeded.
 * Returns null       if the session is still valid, or if no prior session existed
 *                    (e.g. fresh login — timestamps not yet written).
 */
function checkStaleSession() {
  const now            = Date.now();
  const idleExpiry     = _readIdleExpiry();
  const absoluteExpiry = _readAbsoluteExpiry();

  // Check absolute first — takes priority over idle.
  if (absoluteExpiry !== null && now >= absoluteExpiry) return "absolute";
  if (idleExpiry     !== null && now >= idleExpiry)     return "idle";
  return null;
}

/**
 * Return the current idle-expiry timestamp (ms since epoch) from localStorage.
 * Used by SessionWarningModal to drive a timestamp-based live countdown that is
 * immune to background-tab timer throttling.
 * Returns null if the session has not been started or has already ended.
 */
function getIdleExpiry() {
  return _readIdleExpiry();
}

// ─── export ──────────────────────────────────────────────────────────────────

export const sessionManager = {
  start,
  restartFresh,
  end,
  resetTimer,
  onTimeout,
  onWarning,
  onWarningDismiss,
  checkStaleSession,
  getIdleExpiry,
};
